import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import type { TrackContribution } from '@kangaroo/shared';

import {
  fetchTelemetrySessionContext,
  telemetrySessionContextKey,
  updateTelemetrySessionContext,
} from '../telemetryContext';
import {
  circuitOverlaySourceLabel,
  listAssignableCircuitContributions,
  rankSessionCircuitPickerContributions,
  resolveCircuitOverlayForSession,
  suggestCircuitContributionForSession,
  type CircuitMapOverlay,
} from '../../lib/circuitMapOverlay';
import { canUpdateTelemetrySessionContext } from '../../lib/roles';
import type { MyChronSessionExport } from '../../lib/telemetrySession';
import { matchSessionToCircuitDraft, runEnrichmentPipeline } from '../../lib/trackEnrichment';
import { useTrackContributionCatalog } from '../../hooks/useTrackContributionCatalog';
import { useCircuitLapReslice } from '../../hooks/useCircuitLapReslice';
import { sessionContextForCircuitConfiguration } from '../../lib/circuitLapReslice';
import { useAuthStore } from '../../store/authStore';
import { buildCircuitSessionLapOptions, buildSessionLapOptions } from './sessionContent';

export type LapTimingMode = 'mychron' | 'circuit';

export function useSessionLapSlot(options: {
  sessionId: string | null;
  sessionExport: MyChronSessionExport | null | undefined;
  lapTimingMode: LapTimingMode;
  sessionTrackName?: string | null;
  readOnly?: boolean;
  publicDemo?: boolean;
  compareMode?: boolean;
  externalCircuitOverlay?: CircuitMapOverlay | null;
}) {
  const {
    sessionId,
    sessionExport,
    lapTimingMode,
    sessionTrackName = null,
    readOnly = false,
    publicDemo = false,
    compareMode = false,
    externalCircuitOverlay = null,
  } = options;
  const user = useAuthStore((state) => state.user);
  const canEdit =
    !readOnly && !compareMode && user ? canUpdateTelemetrySessionContext(user.role) : false;
  const { data: sessionContext, mutate: mutateContext } = useSWR(
    sessionId && !compareMode ? telemetrySessionContextKey(sessionId) : null,
    () => fetchTelemetrySessionContext(sessionId!),
  );

  const {
    contributions: catalogContributions,
    isLoading: catalogContributionsLoading,
    isHydrating: catalogContributionsHydrating,
  } = useTrackContributionCatalog({
    enabled: !compareMode && !publicDemo && Boolean(sessionExport),
    contributionIds: [
      sessionContext?.contributionId,
      sessionContext?.circuitLapSet?.contributionId,
    ],
    trackId: sessionContext?.trackId,
    sessionGps: sessionExport?.gps,
  });

  const demoContribution = useMemo(() => {
    if (!publicDemo || !sessionExport) {
      return null;
    }

    const pipeline = runEnrichmentPipeline(sessionExport, 'demo:a_0180');
    if (!pipeline?.contribution) {
      return null;
    }

    return {
      ...pipeline.contribution,
      id: 'demo-circuit-a_0180',
      teamId: 'demo',
      trackId: pipeline.matchedTrack.id,
      sessionKey: 'demo:a_0180',
      venue: pipeline.venue || pipeline.matchedTrack.name || 'Demo circuit',
      status: 'merged' as const,
      createdByUserId: 'demo',
      updatedAt: pipeline.contribution.createdAt,
    } satisfies TrackContribution;
  }, [publicDemo, sessionExport]);

  const contributions = useMemo(
    () => (demoContribution ? [demoContribution] : catalogContributions),
    [catalogContributions, demoContribution],
  );

  const contributionsLoading = compareMode || publicDemo ? false : catalogContributionsLoading;

  const assignableCircuits = useMemo(
    () => (compareMode ? [] : listAssignableCircuitContributions(contributions)),
    [compareMode, contributions],
  );

  const circuitPickerOptions = useMemo(() => {
    if (compareMode) {
      return [];
    }

    const ranked = rankSessionCircuitPickerContributions(contributions, {
      assignedContributionId: sessionContext?.contributionId,
      session: sessionExport ?? null,
      sessionTrackName,
      trackId: sessionContext?.trackId,
      // Keep the relevance ranking, but expose the complete assignable catalog
      // so the searchable picker is never limited to only nearby suggestions.
      limit: assignableCircuits.length,
    });
    const assignedContributionId = sessionContext?.contributionId;
    if (
      !assignedContributionId ||
      ranked.some((entry) => entry.contribution.id === assignedContributionId)
    ) {
      return ranked;
    }

    const assigned = contributions.find(
      (contribution) => contribution.id === assignedContributionId,
    );
    return assigned
      ? [{ contribution: assigned, distanceKm: null, recent: false }, ...ranked]
      : ranked;
  }, [
    compareMode,
    assignableCircuits.length,
    contributions,
    sessionContext?.contributionId,
    sessionContext?.trackId,
    sessionExport,
    sessionTrackName,
  ]);

  const suggestedCircuitMatch = useMemo(
    () =>
      compareMode || !sessionExport || sessionContext?.contributionId
        ? null
        : matchSessionToCircuitDraft(sessionExport, assignableCircuits),
    [assignableCircuits, compareMode, sessionContext?.contributionId, sessionExport],
  );

  const suggestedCircuit = useMemo(
    () =>
      compareMode
        ? null
        : suggestCircuitContributionForSession({
            session: sessionExport ?? null,
            contributions,
            sessionTrackName,
            trackId: sessionContext?.trackId,
          }),
    [compareMode, contributions, sessionContext?.trackId, sessionExport, sessionTrackName],
  );

  const resolvedCircuit = useMemo(() => {
    if (compareMode) {
      return {
        overlay: externalCircuitOverlay,
        contributionId: externalCircuitOverlay?.contributionId ?? null,
        source: externalCircuitOverlay ? ('assigned' as const) : null,
      };
    }

    return resolveCircuitOverlayForSession({
      contributions,
      requestedContributionId: null,
      assignedContributionId: sessionContext?.contributionId,
      trackId: sessionContext?.trackId,
      savedCircuitLapSet: sessionContext?.circuitLapSet,
      session: sessionExport ?? null,
    });
  }, [
    compareMode,
    contributions,
    externalCircuitOverlay,
    sessionContext?.circuitLapSet,
    sessionContext?.contributionId,
    sessionContext?.trackId,
    sessionExport,
  ]);

  const circuitLapReslice = useCircuitLapReslice({
    session: sessionExport ?? null,
    sessionKey: sessionId ?? '',
    circuitOverlay: resolvedCircuit.overlay,
    savedCircuitLapSet: compareMode ? null : sessionContext?.circuitLapSet,
    canPersist: canEdit && Boolean(sessionId),
    skipInitialReslice: compareMode,
    onSaved: () => {
      void mutateContext();
    },
  });

  const assignCircuit = useCallback(
    async (contributionId: string | null) => {
      if (!sessionId || !canEdit) {
        return;
      }

      const contribution = assignableCircuits.find((entry) => entry.id === contributionId) ?? null;
      await updateTelemetrySessionContext(sessionId, {
        dataFileId: sessionId,
        contributionId,
        trackId: contribution?.trackId ?? null,
      });
      await mutateContext();
      circuitLapReslice.regenerate();
    },
    [assignableCircuits, canEdit, circuitLapReslice, mutateContext, sessionId],
  );

  const mychronLaps = useMemo(
    () => buildSessionLapOptions(sessionExport, resolvedCircuit.overlay),
    [resolvedCircuit.overlay, sessionExport],
  );

  const circuitLaps = useMemo(
    () => buildCircuitSessionLapOptions(circuitLapReslice.activeReslice),
    [circuitLapReslice.activeReslice],
  );

  const laps = lapTimingMode === 'mychron' ? mychronLaps : circuitLaps;

  const selectedLapNumber =
    lapTimingMode === 'circuit' ? circuitLapReslice.selectedLapNumber : null;

  const assignedContribution = useMemo(
    () => assignableCircuits.find((entry) => entry.id === sessionContext?.contributionId) ?? null,
    [assignableCircuits, sessionContext?.contributionId],
  );

  const coherentSessionContext = useMemo(
    () =>
      sessionContextForCircuitConfiguration(
        sessionContext,
        circuitLapReslice.hasSaved ? (sessionContext?.circuitLapSet ?? null) : null,
      ),
    [circuitLapReslice.hasSaved, sessionContext],
  );

  const previewContribution = useMemo(() => {
    if (assignedContribution || !resolvedCircuit.contributionId) {
      return null;
    }

    return assignableCircuits.find((entry) => entry.id === resolvedCircuit.contributionId) ?? null;
  }, [assignedContribution, assignableCircuits, resolvedCircuit.contributionId]);

  return {
    sessionContext: coherentSessionContext,
    contributionsLoading,
    contributionsHydrating: catalogContributionsHydrating,
    assignableCircuits,
    circuitPickerOptions,
    assignedContribution,
    previewContribution,
    suggestedCircuit,
    suggestedCircuitMatch,
    assignCircuit,
    canEdit,
    resolvedCircuit,
    circuitLapReslice,
    mychronLaps,
    circuitLaps,
    laps,
    selectedLapNumber,
    circuitSourceLabel: circuitOverlaySourceLabel(resolvedCircuit.source),
    hasCircuitOverlay: Boolean(resolvedCircuit.overlay?.startFinishLine),
    publicDemo,
  };
}
